import { InfoCircleOutlined, SwapOutlined } from "@ant-design/icons";
import { Button, Divider, Modal, Radio, Spin, Typography } from "antd";
import dayjs from "dayjs";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DateListPicker } from "~/components/form/DateListPicker.jsx";
import { $t } from "~/i18n.js";
import { api } from "~/lib/api.js";
import { getAllDays } from "~/redux/days/days.actions.js";

const { Text } = Typography;

export const RuilDienstModal = ({ open, onClose, items }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.persist.profile);
  const allDays = useSelector((state) => state.days.allDays);

  const futureDays = allDays
    .filter((day) => day.datum >= dayjs().format("YYYY-MM-DD"))
    .sort((a, b) => (a.datum > b.datum ? 1 : -1));

  // Jouw dienst: selected from items (days where you already have diensten)
  const [jouwDagId, setJouwDagId] = useState(null);
  const [jouwDienstId, setJouwDienstId] = useState(null);

  // Andere dienst: loaded via API for a chosen day
  const [andereDagId, setAndereDagId] = useState(null);
  const [andereDiensten, setAndereDiensten] = useState([]);
  const [loadingAndere, setLoadingAndere] = useState(false);
  const [andereDienstId, setAndereDienstId] = useState(null);

  const [submitting, setSubmitting] = useState(false);

  const jouwDag = items.find((d) => d.dag_id === jouwDagId);
  const jouwDiensten = jouwDag?.diensten || [];

  const onJouwDagChange = (dag_id) => {
    setJouwDagId(dag_id);
    const dag = items.find((d) => d.dag_id === dag_id);
    // Auto-select if there is only one dienst
    setJouwDienstId(dag?.diensten?.length === 1 ? dag.diensten[0].id : null);
  };

  const onAndereDagChange = async (dag_id) => {
    setAndereDagId(dag_id);
    setAndereDienstId(null);
    setAndereDiensten([]);
    if (!dag_id) return;
    const dag = futureDays.find((d) => d.dag_id === dag_id);
    if (!dag) return;
    setLoadingAndere(true);
    const res = await api.get(`days/get_diensten.json?date=${dag.datum}`);
    if (res) setAndereDiensten(res);
    setLoadingAndere(false);
  };

  const handleSwap = async () => {
    setSubmitting(true);
    const res = await api.post("rooster/swap_dienst.json", {
      dienst_a_id: jouwDienstId,
      dienst_b_id: andereDienstId,
    });
    if (res) {
      Modal.success({
        title: $t("Dienst geruild"),
        content: $t("De dienst is geruild en een email is verstuurd."),
      });
      dispatch(getAllDays());
      handleClose();
    }
    setSubmitting(false);
  };

  const handleClose = () => {
    setJouwDagId(null);
    setJouwDienstId(null);
    setAndereDagId(null);
    setAndereDiensten([]);
    setAndereDienstId(null);
    onClose();
  };

  const canSwap = !!(jouwDienstId && andereDienstId);

  return (
    <Modal
      title={$t("Ruil dienst")}
      open={open}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          {$t("Annuleren")}
        </Button>,
        <Button
          key="swap"
          type="primary"
          icon={<SwapOutlined />}
          disabled={!canSwap}
          loading={submitting}
          onClick={handleSwap}
        >
          {$t("Ruil dienst")}
        </Button>,
      ]}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Jouw dienst */}
        <div>
          <Text strong>{$t("Jouw dienst")}</Text>

          <div style={{ marginTop: 8 }}>
            <DateListPicker
              value={jouwDagId}
              onChange={onJouwDagChange}
              options={items.map((item) => ({
                value: item.dag_id,
                label: dayjs(item.datum).format("ddd D MMM YYYY"),
              }))}
              placeholder={$t("Selecteer datum")}
            />
          </div>
          <Radio.Group
            style={{
              marginTop: 8,
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
            value={jouwDienstId}
            onChange={(e) => setJouwDienstId(e.target.value)}
          >
            {jouwDiensten.map((d) => (
              <Radio key={d.id} value={d.id}>
                {d.name}
              </Radio>
            ))}
          </Radio.Group>
        </div>

        <Divider style={{ margin: 0 }} />

        {/* Andere dienst */}
        <div>
          <Text strong>{$t("Andere dienst")}</Text>

          <div style={{ marginTop: 8 }}>
            <DateListPicker
              value={andereDagId}
              onChange={onAndereDagChange}
              options={futureDays.map((day) => ({
                value: day.dag_id,
                label: dayjs(day.datum).format("ddd D MMM YYYY"),
              }))}
              placeholder={$t("Selecteer datum")}
            />
          </div>

          {loadingAndere && <Spin size="small" style={{ marginTop: 8 }} />}

          {!loadingAndere && andereDiensten.length > 0 && (
            <Radio.Group
              style={{
                marginTop: 8,
                display: "flex",
                flexDirection: "column",
                gap: 4,
              }}
              value={andereDienstId}
              onChange={(e) => setAndereDienstId(e.target.value)}
            >
              {andereDiensten.map((d) => (
                <Radio key={d.id} value={d.id} disabled={d.user_id === user.id}>
                  {d.name}
                  {" — "}
                  <Text type="secondary">{d.user_name || $t("OPEN")}</Text>
                </Radio>
              ))}
            </Radio.Group>
          )}

          {!loadingAndere && andereDagId && andereDiensten.length === 0 && (
            <Text type="secondary" style={{ marginTop: 8, display: "block" }}>
              {$t("Geen diensten gevonden voor deze datum.")}
            </Text>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <InfoCircleOutlined style={{ color: "#888" }} />
          <Text type="secondary">
            {$t("Een email zal worden verzonden naar de andere gebruiker.")}
          </Text>
        </div>
      </div>
    </Modal>
  );
};
